import React, { forwardRef, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from '@phosphor-icons/react';
import { cn } from '../../../lib/cn';
import { Icon } from '../../Icon';
import { useViewport } from '../../../hooks/useViewport';
import { Sheet, SheetBody, SheetFooter, SheetHeader } from '../../mobile/organisms/Sheet';
import { mergeRefs, useModalLayer } from './focus';
import { motionDuration } from './platform';
import '../../../patterns/motion.css';

/**
 * Drawer, the 460px right slide-over.
 *
 * CSS: `desktop/system.css` `.dsk-drawer-scrim`, `.dsk-drawer` and all seven
 * sub-parts: `__header`, `__heading`, `__kicker`, `__title`, `__body`,
 * `__section`, `__footer`. Plus `.dsk-scroll-locked` on <body>.
 * Behaviour: ports BEHAVIOUR #7 from
 * `behaviours/desktop-behaviours.reference.js` in full.
 *
 * Everything #7 owes the user, kept:
 *   scrim that closes on click  ·  Escape closes  ·  focus moves into the panel
 *   on open and RETURNS to the trigger on close  ·  Tab is trapped inside the
 *   panel  ·  the page behind cannot scroll and the scrollbar gap is padded
 *   back so the layout does not jump  ·  role="dialog" + aria-modal + a
 *   labelled title  ·  prefers-reduced-motion respected (the CSS drops the
 *   transition, this drops the matching wait).
 *
 * The declarative `[data-drawer-open="id"]` / `[data-drawer-close]` contract is
 * NOT ported: in React the trigger is a component that owns `open` state, and a
 * document-level click delegation on top of that is a second source of truth.
 * Focus return does not need the trigger to be found by attribute either, since
 * `useModalLayer` captures `document.activeElement` at open, which is exactly
 * what the reference falls back to.
 *
 * The panel stays mounted through the exit so the slide-out actually plays,
 * then unmounts. Under reduced motion that wait is zero. See `DRAWER_MS`.
 *
 * BELOW 1024 THIS IS NOT A SIDE PANEL AT ALL
 * ------------------------------------------
 * A 460px slide-over on a 390px phone is a full-screen page wearing a drawer's
 * clothes: it covers everything, it arrives from the wrong edge, and its close
 * affordance is a 32px target in the far corner. The mobile track already owns
 * the right surface for this, the `Sheet` organism, so below the handoff this
 * component MOUNTS THAT, one tree only, chosen by `useViewport()` exactly as
 * `patterns/market` and `CartOverlay.view` choose theirs. Nothing is rendered
 * twice and hidden with CSS.
 *
 * That is what moves order history, view-orders and every other
 * `OrderDetailsDrawer` call site onto a bottom sheet on a phone without a
 * single call site changing: they all go through here.
 *
 * `Sheet` is reused whole rather than reimplemented inside this file. It
 * already brings the focus trap, the refcounted scroll lock, Escape, scrim
 * dismissal and the grabber; a second implementation of all five living in the
 * desktop track is the drift this design system exists to prevent. The only
 * thing added on top is `position: fixed` (see `patterns/motion.css`), because
 * this component portals and a Sheet used directly does not.
 *
 * MOTION for both surfaces lives in `patterns/motion.css`, including why the
 * entry is an `animation` rather than a transition out of a hidden resting
 * state. That file is required reading before touching the classes below.
 */

/* The unmount wait, and it is NOT `--ds-transition-base` any more. Behaviour #7
   tied it to that token because the exit was a 200ms library TRANSITION; the
   exit is a keyframe animation owned by `patterns/motion.css` now, and its
   `--dsa-motion-exit` went 180ms -> 280ms to stop the drawer snapping out. This
   number tracks THAT one, 40ms clear of it: the wait must outlast the animation
   or a single slow frame unmounts the node mid-slide and the panel vanishes
   instead of leaving. Was 200ms against a 180ms exit, which left only 20ms --
   under a frame and a half at 60Hz, and less than that on a loaded page. */
const DRAWER_MS = 320;

export const Drawer = forwardRef(function Drawer(
  {
    open,
    onClose,
    kicker,
    title,
    children,
    footer,
    closeOnEsc = true,
    closeOnScrim = true,
    showClose = true,
    closeLabel = 'Close',
    ariaLabel = 'Panel',
    initialFocusRef,
    scrimClassName,
    className,
    ...rest
  },
  ref,
) {
  const panelRef = useRef(null);
  const titleId = useId();
  const { isDesktop } = useViewport();

  /* Two flags, not one. `mounted` keeps the node in the DOM through the exit
     animation; `exiting` is the class that plays it.

     There is no rAF here any more, and its absence is the point. The old code
     waited a frame before adding `.is-open` because the entry was a TRANSITION
     out of `translateX(100%)` and a transition needs a laid-out from-state. The
     entry is a keyframe animation now (see patterns/motion.css), which needs no
     such priming — and the frame that rAF bought was a frame with the panel
     already mounted but not yet revealed, i.e. a flash of the drawer sitting at
     its resting position. */
  const [mounted, setMounted] = useState(Boolean(open));
  const [exiting, setExiting] = useState(false);

  /* Opening is adjusted DURING RENDER so the panel is in the DOM on the very
     commit that `open` turns true, which is what `useModalLayer` needs in order
     to find something to focus. Mirrors the Sheet, deliberately: the two halves
     of one surface must not have two different mount timelines. */
  if (open && isDesktop && (!mounted || exiting)) {
    setMounted(true);
    setExiting(false);
  }

  useEffect(() => {
    /* Below the handoff the Sheet owns its own mount/exit lifecycle, so a
       second timer here would race it and could rip the sheet out mid-slide. */
    if (!isDesktop) return undefined;
    if (open || !mounted) return undefined;

    setExiting(true);
    const timer = setTimeout(() => {
      setExiting(false);
      setMounted(false);
    }, motionDuration(DRAWER_MS));
    return () => clearTimeout(timer);
  }, [open, mounted, isDesktop]);

  useModalLayer({
    /* Desktop only. The Sheet brings its own focus trap, Escape handler and
       refcounted scroll lock; running both would double-lock the body and put
       two traps on the same node fighting over where Tab lands. */
    active: Boolean(open) && isDesktop,
    panelRef,
    onEscape: closeOnEsc ? onClose : undefined,
    initialFocusRef,
  });

  if (typeof document === 'undefined') return null;

  if (!isDesktop) {
    return createPortal(
      <Sheet
        ref={ref}
        open={Boolean(open)}
        onClose={onClose}
        closeOnEscape={closeOnEsc}
        closeOnScrimClick={closeOnScrim}
        initialFocusRef={initialFocusRef}
        aria-label={title ? undefined : ariaLabel}
        /* Fixed rather than absolute, because this portals to <body>. The
           caller's `className` is deliberately NOT forwarded here: it is
           written against `.dsk-drawer`, and a width or a right-edge rule
           aimed at a 460px side panel is wrong on a full-bleed sheet. */
        className="dsa-drawer-sheet"
        scrimClassName={cn('dsa-drawer-scrim', scrimClassName)}
        {...rest}
      >
        {/* Kicker becomes the subtitle, so it reads BELOW the title here rather
            than above it. A sheet header has no over-line slot, and inventing
            one would fork the mobile header anatomy for one caller. The words
            are the same words. */}
        <SheetHeader
          title={title}
          subtitle={kicker}
          trailing={
            showClose ? (
              <button type="button" className="ds-iconbtn" aria-label={closeLabel} onClick={onClose}>
                <Icon as={X} />
              </button>
            ) : null
          }
        />
        <SheetBody>{children}</SheetBody>
        {/* Stacked, not side by side: a drawer footer carries one full-width
            commit ("View cart", "Confirm order"), which is the form-sheet case
            the stacked modifier exists for. */}
        {footer ? <SheetFooter stacked>{footer}</SheetFooter> : null}
      </Sheet>,
      document.body,
    );
  }

  if (!mounted) return null;

  return createPortal(
    <>
      {/* `.is-open` stays on for the whole life of the node now, exit included.
          It is the library's transition-driven reveal and the animation has
          taken that job over; what it still does is hold the panel at its
          resting position, which is the state both animations start and end
          against. */}
      <div
        className={cn(
          'dsk-drawer-scrim',
          'is-open',
          'dsa-motion-scrim',
          exiting ? 'dsa-motion-scrim--out' : 'dsa-motion-scrim--in',
          scrimClassName,
        )}
        role="presentation"
        onMouseDown={!exiting && closeOnScrim ? onClose : undefined}
      />
      <aside
        ref={mergeRefs(panelRef, ref)}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        aria-label={title ? undefined : ariaLabel}
        aria-hidden={exiting || undefined}
        tabIndex={-1}
        className={cn(
          'dsk-drawer',
          'is-open',
          'dsa-motion-panel',
          exiting ? 'dsa-motion-panel--out' : 'dsa-motion-panel--in',
          className,
        )}
        {...rest}
      >
        <header className="dsk-drawer__header">
          <div className="dsk-drawer__heading">
            {/* Kicker above title, the same anatomy the card and the listing
                row use. */}
            {kicker ? <p className="dsk-drawer__kicker">{kicker}</p> : null}
            {title ? (
              <h2 id={titleId} className="dsk-drawer__title">
                {title}
              </h2>
            ) : null}
          </div>
          {showClose ? (
            <button type="button" className="dsk-iconbtn" aria-label={closeLabel} onClick={onClose}>
              <Icon as={X} />
            </button>
          ) : null}
        </header>

        <div className="dsk-drawer__body">{children}</div>

        {/* Exactly one ink control lives here; the escape hatch stays ghost. */}
        {footer ? <footer className="dsk-drawer__footer">{footer}</footer> : null}
      </aside>
    </>,
    document.body,
  );
});

/**
 * DrawerSection, the quiet uppercase label inside a drawer body.
 * CSS: `.dsk-drawer__section`.
 */
export const DrawerSection = forwardRef(function DrawerSection({ children, className, ...rest }, ref) {
  return (
    <p ref={ref} className={cn('dsk-drawer__section', className)} {...rest}>
      {children}
    </p>
  );
});

export default Drawer;
